import { Popover, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import cn from "classnames";
import Button from "@elements/button";
import Slider from "rc-slider";
import Accordion from "@elements/accordion";
import { IFilterInput, IFilterOutput } from "types/api/product";
import { useTranslations } from "next-intl";
import { ChevronDownIcon } from "@heroicons/react/solid";
import { useRouter } from "next/router";
import { convertNumber } from "@utils/digits";

interface Props {
  items: IFilterInput[];
  defaultFilters?: IFilterOutput[];
  action: (data: IFilterOutput[]) => void;
}

const Filters: React.FC<Props> = ({ items, defaultFilters, action }) => {
  const t = useTranslations();
  const [filters, setFilters] = useState<IFilterOutput[]>([]);
  const { locale } = useRouter();

  useEffect(() => {
    if (defaultFilters) {
      setFilters([...defaultFilters]);
    } else {
      setFilters([]);
    }
  }, [defaultFilters]);

  const handelFilters = (data: IFilterInput, item: number) => {
    const _filters = [...filters];
    const target = _filters.find((f) => f.key === data.key);
    const _target = _filters.filter((f) => f.key !== data.key);

    if (target) {
      updateFilters(data, item, target, _target)
    } else {
      setFilters([
        ..._filters,
        {
          key: data.key,
          type: data.type,
          id: data.id,
          operation_type: data.operation_type,
          items: [item],
          items_operation: data.items_operation,
        },
      ]);
    }
  };


  const updateFilters = (data: IFilterInput, item: number, target: IFilterOutput, _target: IFilterOutput[]) => {
    if (data.type === "singleSelect") {
      const newFilter = target.items?.includes(item)
        ? _target
        : [..._target, { ...target, items: [item] }];
      setFilters(newFilter);
    } else if (data.type === "multiSelect") {
      if (target.items?.includes(item)) {
        const index = target.items.indexOf(item);
        target.items.splice(index, 1);
        const newFilter = target.items.length ? [..._target, { ...target }] : _target;
        setFilters(newFilter);
      } else {
        target.items?.push(item);
        setFilters([..._target, { ...target }]);
      }
    }
  }


  const handelRangeFilter = (data: IFilterInput, range: number[]) => {
    const _filters = [...filters];
    const target = _filters.find((f) => f.key === data.key);
    const _target = _filters.filter((f) => f.key !== data.key);
    if (target) {
      target.range = { min: range[0], max: range[1] };

      setFilters([..._target, { ...target }]);
    } else {
      setFilters([
        ..._filters,
        {
          key: data.key,
          type: data.type,
          id: data.id,
          operation_type: data.operation_type,
          range: { min: range[0], max: range[1] },
          items_operation: data.items_operation,
        },
      ]);
    }
  };

  const resetAll = () => {
    setFilters([]);
    action([]);
  };

  const handelSubmitFilters = (close: () => void) => {
    const finalFilter = [];
    for (const iterator of filters) {
      let data = { ...iterator };
      delete data.id;
      finalFilter.push(data);
    }
    action(finalFilter);
    close();
  };

  return (
    <Popover className={cn("relative")} as="nav">
      {({ close }) => (
        <>
          <Transition
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Popover.Overlay className="fixed inset-0 backdrop-brightness-50 z-[25]" />
          </Transition>
          <Popover.Button className="text-gray-500 z-[20]  h-9  relative focus:outline-none ">
            <div className=" flex w-max text-sm">
              <p className="block truncate text-[#008AF6] font-normal">
                {t("w-filters")}
              </p>
              <span className=" flex items-center px-2 pointer-events-none">
                <ChevronDownIcon
                  className="w-5 h-5 text-[#008AF6]"
                  aria-hidden="true"
                />
              </span>
            </div>

          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 -translate-x-full"
            enterTo="opacity-100 -translate-x-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 -translate-x-0"
            leaveTo="opacity-0 -translate-x-full"
          >
            <Popover.Panel className=" flex flex-col justify-between fixed height-screen top-[0px] z-[60] left-0  w-screen overflow-hidden shadow-lg ring-1 ring-black ring-opacity-5 max-w-xs  bg-white transform">
              <div className="w-full flex flex-wrap justify-between items-center h-16 fixed top-0 p-3 shadow bg-white z-10">
                <p className=" font-bold text-xl">
                  {t("w-filters")} {filters.length ? `(${filters.length})` : ""}
                </p>
                <button
                  className=" text-[#008AF6] font-normal text-sm"
                  onClick={() => resetAll()}
                >
                  {t("w-resetAll")}
                </button>
              </div>
              <div className="w-full mt-[65px]  h-[calc(100vh-160px)] overflow-auto">
                {items.map((item, index) => (
                  <div
                    className={`w-full pt-2 pb-2 px-1 ${index + 1 === items.length ? "" : "border-b"
                      }`}
                    key={`${item.title}-${item.key}`}
                  >
                    <Accordion
                      title={`${item.title} ${filters.find((f) => f.key === item.key)?.range
                        ? `(${filters.find((f) => f.key === item.key)?.range
                          ?.min
                        } - ${filters.find((f) => f.key === item.key)?.range
                          ?.max
                        })`
                        : ""
                        }`}
                      open={
                        filters.find((f) => f.key === item.key) ? true : false
                      }
                    >
                      <div className="w-full  flex flex-wrap px-2">
                        {item.type === "singleSelect" &&
                          item.items?.map((f) => (
                            <button
                              className={`${filters.find((f) => f.id === item.id) &&
                                filters
                                  .find((f) => f.id === item.id)
                                  ?.items?.includes(f.value)
                                ? " border-[#008AF6] text-[#008AF6] border bg-[#008AF6] bg-opacity-10"
                                : "border-[#eee] text-[#AAA] border"
                                }  rounded-[20px]  h-10  px-5 mb-2 font-normal text-xs mr-2 !w-max`}
                              key={`${f.title}-${f.slug}`}
                              onClick={() => handelFilters(item, f.value)}
                            >
                              {convertNumber(locale, f.title)}
                            </button>
                          ))}

                        {item.type === "range" && (
                          <div className=" w-[93%] mx-auto">
                            <Slider
                              marks={{
                                [`${item.range?.min}`]: `${item.range?.min}`,
                                [`${item.range?.max}`]: `${item.range?.max}`,
                              }}
                              min={item.range?.min}
                              max={item.range?.max}
                              value={
                                filters.find((f) => f.id === item.id)?.range
                                  ? [
                                    filters.find((f) => f.id === item.id)
                                      ?.range?.min as number,
                                    filters.find((f) => f.key === item.key)
                                      ?.range?.max as number,
                                  ]
                                  : [
                                    item.range?.min as number,
                                    item.range?.max as number,
                                  ]
                              }
                              range
                              onChange={(e: number[] | number) =>
                                handelRangeFilter(item, e as any)
                              }
                            />
                          </div>
                        )}
                        {item.type === "multiSelect" &&
                          item.items?.map((f) => (
                            <div
                              className="w-full flex items-center mb-2"
                              key={`${f.title}-${f.slug}`}
                            >
                              <input
                                type="checkbox"
                                id="1"
                                name={f.title}
                                value={f.value}
                                onClick={() => handelFilters(item, f.value)}
                                checked={
                                  filters.find((f) => f.id === item.id) &&
                                  filters
                                    .find((f) => f.id === item.id)
                                    ?.items?.includes(f.value)
                                }
                              />
                              <label
                                className=" font-normal text-sm text-[#333333] mx-1 capitalize"
                                htmlFor={f.title}
                              >
                                {" "}
                                {f.title}
                              </label>
                            </div>
                          ))}
                      </div>
                    </Accordion>
                  </div>
                ))}

              </div>
              <div className="w-full flex flex-wrap bg-white z-30 justify-between items-center h-20 fixed bottom-0 rounded-t-[32px] shadow-[0px_-8px_32px_rgba(0,0,0,0.12)] p-3">
                <Button
                  size="slim"
                  className="w-full h-12 font-extrabold text-base rounded-full"
                  onClick={() => handelSubmitFilters(close)}
                >
                  {t("w-applyFilters")}
                </Button>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default Filters;
